import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  type?: string;
  color?: string;
  description?: string;
  location?: string;
  doctor?: string;
  startTime?: string;
  endTime?: string;
}

interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
}

@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnInit, OnChanges {
  @Input() events: CalendarEvent[] = [];
  @Input() selectedDate: Date | null = null;
  @Output() dateSelected = new EventEmitter<Date>();
  @Output() eventClicked = new EventEmitter<CalendarEvent>();

  currentDate = new Date();
  displayDate = new Date();
  calendarDays: CalendarDay[] = [];
  weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  
  private monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  ngOnInit() {
    this.generateCalendarDays();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedDate'] && this.selectedDate) {
      this.displayDate = new Date(this.selectedDate);
      this.generateCalendarDays();
    }
  }

  generateCalendarDays() {
    this.calendarDays = [];
    
    const year = this.displayDate.getFullYear();
    const month = this.displayDate.getMonth();
    
    // Premier jour du mois
    const firstDay = new Date(year, month, 1);
    // Dernier jour du mois
    const lastDay = new Date(year, month + 1, 0);
    
    // Premier lundi de la semaine contenant le premier jour du mois
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Lundi = 1
    startDate.setDate(firstDay.getDate() + mondayOffset);
    
    // Générer 42 jours (6 semaines)
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = date.getMonth() === month;
      const isToday = this.isSameDay(date, new Date());
      const isSelected = this.selectedDate ? this.isSameDay(date, this.selectedDate) : false;
      
      this.calendarDays.push({
        date: date,
        dayNumber: date.getDate(),
        isCurrentMonth: isCurrentMonth,
        isToday: isToday,
        isSelected: isSelected
      });
    }
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  previousMonth() {
    this.displayDate.setMonth(this.displayDate.getMonth() - 1);
    this.generateCalendarDays();
  }

  nextMonth() {
    this.displayDate.setMonth(this.displayDate.getMonth() + 1);
    this.generateCalendarDays();
  }

  goToToday() {
    this.displayDate = new Date();
    this.selectDate(new Date());
  }

  selectDate(date: Date) {
    this.selectedDate = date;
    this.dateSelected.emit(date);
    this.generateCalendarDays(); // Regénérer pour mettre à jour la sélection
  }

  getCurrentMonthYear(): string {
    return `${this.monthNames[this.displayDate.getMonth()]} ${this.displayDate.getFullYear()}`;
  }

  getDayClasses(day: CalendarDay): string {
    let classes = 'hover:bg-gray-50 rounded';
    
    if (!day.isCurrentMonth) {
      classes += ' text-gray-400';
    } else {
      classes += ' text-gray-900';
    }
    
    return classes;
  }

  getDayNumberClasses(day: CalendarDay): string {
    let classes = 'w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors';
    
    if (day.isSelected) {
      classes += ' bg-blue-500 text-white';
    } else if (day.isToday) {
      classes += ' text-blue-600 font-semibold';
    } else if (!day.isCurrentMonth) {
      classes += ' text-gray-400';
    } else {
      classes += ' text-gray-900 hover:bg-gray-100';
    }
    
    return classes;
  }

  getSelectedDateFormatted(): string {
    if (!this.selectedDate) return '';
    
    const options: Intl.DateTimeFormatOptions = { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    };
    
    return this.selectedDate.toLocaleDateString('fr-FR', options);
  }

  getDayTooltip(date: Date): string {
    const events = this.getEventsForDate(date);
    if (events.length === 0) return '';
    
    const dateStr = date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
    
    const eventList = events.map(event => {
      const time = event.date.toLocaleTimeString('fr-FR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      return `• ${time} - ${event.title}`;
    }).join('\n');
    
    return `${dateStr}\n\n${eventList}`;
  }

  getEventsForDate(date: Date): CalendarEvent[] {
    return this.events.filter(event => 
      this.isSameDay(event.date, date)
    );
  }

  onEventClick(event: CalendarEvent, clickEvent: Event) {
    clickEvent.stopPropagation(); // Empêcher la sélection de date
    this.eventClicked.emit(event);
  }

  getEventColor(event: CalendarEvent): string {
    if (event.color) return event.color;
    
    // Couleurs par défaut selon le type
    const colorMap: { [key: string]: string } = {
      'appointment': 'bg-blue-500',
      'vaccination': 'bg-green-500',
      'examination': 'bg-purple-500',
      'treatment': 'bg-red-500',
      'medical_service': 'bg-indigo-500',
      'financial_service': 'bg-yellow-500',
      'default': 'bg-gray-500'
    };
    
    return colorMap[event.type || 'default'];
  }

  formatEventTime(date: Date): string {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

  formatTimeString(timeString: string | undefined): string {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('fr-FR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return timeString;
    }
  }

  getEventTimeRange(event: CalendarEvent): string {
    if (event.startTime && event.endTime) {
      return `${this.formatTimeString(event.startTime)} - ${this.formatTimeString(event.endTime)}`;
    }
    return this.formatEventTime(event.date);
  }
}